import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@/ui/atoms/Button';
import Eyebrow from '@/ui/atoms/Eyebrow';
import { useConsent } from '@/store/consent';

// PersonalizationSettingsSection — P5 privacy fix #3: opt-out / withdraw UI.
// Mounted inside SettingsPanel (Account > Profile in current FE). Per ADDENDUM
// correction 5 this is its own component, not folded into ConsentBanner.
export function PersonalizationSettingsSection() {
  const { t } = useTranslation();
  const state = useConsent((s) => s.state);
  const acceptedAt = useConsent((s) => s.acceptedAt);
  const consentTextVersion = useConsent((s) => s.consentTextVersion);
  const accept = useConsent((s) => s.accept);
  const withdraw = useConsent((s) => s.withdraw);
  const [busy, setBusy] = useState<'accept' | 'withdraw' | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [withdrawn, setWithdrawn] = useState(false);

  const onWithdraw = async () => {
    setBusy('withdraw');
    setErr(null);
    try {
      await withdraw();
      setWithdrawn(true);
    } catch {
      setErr(t('consent.errorWithdraw'));
    } finally {
      setBusy(null);
    }
  };

  const onAccept = async () => {
    setBusy('accept');
    setErr(null);
    try {
      await accept();
    } catch {
      setErr(t('consent.errorAccept'));
    } finally {
      setBusy(null);
    }
  };

  return (
    <section
      className="px-6 py-5 border border-hairline rounded-md bg-paper"
      aria-label={t('personalization.settingsHeading')}
      data-testid="personalization-settings"
    >
      <Eyebrow tone="vermilion" className="mb-2">
        {t('personalization.settingsHeading')}
      </Eyebrow>
      <p className="text-sm text-ink-2 leading-relaxed mb-4">
        {t('personalization.settingsBody')}
      </p>

      <dl className="text-xs text-ink-3 space-y-1 mb-4">
        <div>
          <dt className="inline">state · </dt>
          <dd className="inline font-mono text-ink-2">{state}</dd>
        </div>
        {acceptedAt && (
          <div>
            <dt className="inline">{t('personalization.lastAcceptedAt', { date: acceptedAt.slice(0, 10) })}</dt>
          </div>
        )}
        {consentTextVersion && (
          <div>
            <dt className="inline">consent_text_version · </dt>
            <dd className="inline font-mono text-ink-2">{consentTextVersion}</dd>
          </div>
        )}
      </dl>

      {err && <p className="text-vermilion text-xs mb-3">{err}</p>}
      {withdrawn && (
        <p className="text-jade-700 text-xs mb-3" data-testid="settings-withdrawn">
          {t('consent.withdrawn', { days: 30 })}
        </p>
      )}

      <div className="flex gap-3">
        {state === 'accepted' && (
          <Button
            variant="ghost"
            onClick={onWithdraw}
            loading={busy === 'withdraw'}
            disabled={busy !== null}
            data-testid="settings-withdraw-btn"
          >
            {t('personalization.withdrawCta')}
          </Button>
        )}
        {state !== 'accepted' && (
          <Button
            variant="vermilion"
            onClick={onAccept}
            loading={busy === 'accept'}
            disabled={busy !== null}
            data-testid="settings-accept-btn"
          >
            {t('consent.accept')}
          </Button>
        )}
      </div>
    </section>
  );
}

export default PersonalizationSettingsSection;
